import React, { memo, useState, PropsWithChildren, useCallback } from 'react';
import Div from '@/shared/Div';
import Span from '@/shared/Span';
import Button from '@/shared/Button';
import Heading from '@/shared/Heading';
import Link from 'next/link';
import Map from '../Map';
import classNames from 'classnames';
import ChevronDownIcon from '@/components/Icons/ChevronDownIcon';
import ChevronUpIcon from '@/components/Icons/ChevronUpIcon';
import is from '@/utils/is';
import Container from '@/shared/Container';

interface ListItem {
  key?: string;
  value?: string;
  label?: string;
  href?: string;
}

export interface ProductSpecificationsProps {
  title: string;
  downloadResources?: { title: string; list: ListItem[] };
  technicalInformation?: { title: string; list: ListItem[] };
  additionalFeatures?: { title: string; list: ListItem[] };
  productInformation?: { title: string; list: ListItem[] };
  toggleFunction: (id: number) => void;
}

const ProductSpecificationsComponent = ({
  toggleFunction,
  data,
  activeIndex,
  type,
  id,
}: PropsWithChildren<{
  toggleFunction: (id: number) => void;
  data: { title: string; list: ListItem[] };
  activeIndex: number | null;
  type: string;
  id: number;
}>) => (
  <Div className="border-b border-cti-on-secondary">
    <Button
      className="w-full flex !items-start justify-between text-hd-5 md:text-hd-4 py-s4 md:py-s5 !text-left"
      theme="none"
      dataTestId={`accordion-button-${type}`}
      onClick={() => toggleFunction(id)}
    >
      <Span className="w-[calc(100%-24px)]">{data.title}</Span>
      {activeIndex === id ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </Button>
    {activeIndex === id && (
      <ul className="pb-s5 text-body-base">
        <Map
          list={data.list}
          data-testid="media-wrapper"
          render={(item, index) => {
            return (
              <li
                key={index}
                className={classNames({
                  'mt-2': index !== 0,
                  'flex gap-s5': type !== 'downloadResources',
                })}
              >
                {type === 'downloadResources' ? (
                  <Link className="underline" href={item?.href || '/'} target="_blank">
                    {item.label}
                  </Link>
                ) : (
                  <>
                    <Span className="w-28 md:w-52">{item.key}</Span>
                    <Span className="text-cti-tertiary w-[calc(100%-128px)] md:w-[calc(100%-224px)]">
                      {item.value}
                    </Span>
                  </>
                )}
              </li>
            );
          }}
        />
      </ul>
    )}
  </Div>
);

const ProductSpecifications = (props: PropsWithChildren<ProductSpecificationsProps>) => {
  const { title, downloadResources, technicalInformation, additionalFeatures, productInformation } =
    props;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleProductSpecifications = useCallback(
    (index: number) => {
      setActiveIndex(activeIndex === index ? null : index);
    },
    [activeIndex],
  );

  const list = [
    { ...(additionalFeatures ?? {}), type: 'additionalFeatures' },
    { ...(technicalInformation ?? {}), type: 'technicalInformation' },
    { ...(productInformation ?? {}), type: 'productInformation' },
    { ...(downloadResources ?? {}), type: 'downloadResources' },
  ];

  const renderItem = useCallback(
    (item: any, index: number) => {
      if (item && item.title && is.not.empty.array(item.list)) {
        return (
          <ProductSpecificationsComponent
            key={index}
            toggleFunction={toggleProductSpecifications}
            activeIndex={activeIndex}
            data={item}
            type={item.type}
            id={index + 1}
          />
        );
      }
      return null;
    },
    [activeIndex, toggleProductSpecifications],
  );
  if (
    title &&
    (additionalFeatures?.list?.length ||
      technicalInformation?.list?.length ||
      technicalInformation?.list?.length ||
      productInformation?.list?.length ||
      downloadResources?.list?.length)
  ) {
    return (
      <Div className="bg-cm-subtle py-s6  md:py-10  text-cm-primary">
        <Container>
          {title && (
            <Heading
              level={2}
              className="text-h-3 md:text-hd-2 border-b border-cti-on-secondary pb-s4"
            >
              {title}
            </Heading>
          )}
          <Map list={list} render={renderItem} />
        </Container>
      </Div>
    );
  }

  return null;
};

export default memo(ProductSpecifications);
